import { useAppDispatch } from '@hooks/reduxHooks'
import { deleteInsiBatchCheckRequest } from '@state/thunks/insiThunk'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { InsiVerificationBatchRequestDTO } from '@services/InsiService'
import { TrashIcon } from 'lucide-react'
import moment from 'moment'
import React, { useCallback, useState } from 'react'
import InsiBatchResultDetails from './InsiBatchResultDetails'

const formatSchedule = (value: string) => {
  if (!value) {
    return 'Non défini'
  }
  const schedule = moment(value)
  return schedule.isValid() ? schedule.format('DD/MM/YYYY HH:mm') : 'Non défini'
}

type StatusType = 'completed' | 'ready' | 'pending'

const buildStatus = (request: InsiVerificationBatchRequestDTO) => {
  const hasResult = Boolean(request.result?.message?.resultats)
  if (hasResult) {
    return { type: 'completed' as StatusType }
  }

  if (request.dateToStartResultCheck) {
    const scheduledMoment = moment(request.dateToStartResultCheck)
    scheduledMoment.add(request.delay, 'seconds')
    console.log('scheduledMoment', scheduledMoment.toDate())

    if (scheduledMoment.isValid() && scheduledMoment.isBefore(moment())) {
      return { type: 'ready' as StatusType }
    }
  }

  return { type: 'pending' as StatusType }
}

interface InsiBatchRequestListProps {
  requests: InsiVerificationBatchRequestDTO[]
  onRetrieveResults?: (
    request: InsiVerificationBatchRequestDTO,
  ) => Promise<void> | void
  retrievingBatchId?: string | null
  canRetrieve?: boolean
}

export const InsiBatchRequestList: React.FC<InsiBatchRequestListProps> = ({
  requests,
  onRetrieveResults,
  canRetrieve = true,
}) => {
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [isRetrieving, setIsRetrieving] = useState(false)

  const dispatch = useAppDispatch()
  const handleRetrieveClick = useCallback(
    async (request: InsiVerificationBatchRequestDTO) => {
      if (!onRetrieveResults) {
        return
      }
      setIsRetrieving(true)
      try {
        await onRetrieveResults(request)
      } finally {
        setIsRetrieving(false)
      }
    },
    [onRetrieveResults],
  )

  if (!requests.length) {
    return null
  }

  const handleBatchDelete = async (batchId: string) => {
    await dispatch(deleteInsiBatchCheckRequest(batchId))
    setOpenDeleteDialog(false)
  }

  return (
    <Box className="w-[420px] flex-shrink-0" sx={{ backgroundColor: 'white' }}>
      <Box
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            padding: 2,
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Vérifications INSi en attente
          </Typography>
          <Typography variant="caption" color="primary.contrastText">
            {requests.length} lot
            {requests.length > 1 ? 's' : ''} en cours
          </Typography>
        </Box>
        <Box
          sx={{
            paddingX: 2,
            paddingY: 1,
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto',
          }}
        >
          {requests.map((request, index) => {
            const status = buildStatus(request)
            const patientCountLabel = `${request.submittedList.length} patient${
              request.submittedList.length > 1 ? 's' : ''
            }`
            const statusChip =
              status.type === 'completed'
                ? { label: 'Résultat disponible', color: 'success' as const }
                : { label: 'En attente', color: 'info' as const }

            const isButtonDisabled = !canRetrieve || isRetrieving

            return (
              <React.Fragment key={request.batchId}>
                <Box className="flex flex-col gap-2 py-3">
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      Lot #{request.batchId}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={patientCountLabel}
                        size="small"
                        variant="outlined"
                      />
                      {status.type === 'ready' ? (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleRetrieveClick(request)}
                          disabled={isButtonDisabled}
                          startIcon={
                            isRetrieving ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : undefined
                          }
                        >
                          {isRetrieving
                            ? 'Récupération...'
                            : 'Récupérer résultats'}
                        </Button>
                      ) : (
                        <Chip
                          label={statusChip.label}
                          size="small"
                          color={statusChip.color}
                        />
                      )}
                    </Stack>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Delai : {request.delay} seconds
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Soumis le : {formatSchedule(request.createdAt)}
                  </Typography>
                  {request.codeReturned && (
                    <Typography variant="caption" color="text.secondary">
                      Code retour : {request.codeReturned}
                    </Typography>
                  )}
                  {request.result && (
                    <Box sx={{ mt: 1.5 }}>
                      <InsiBatchResultDetails
                        result={request.result}
                        submittedList={request.submittedList}
                      />
                    </Box>
                  )}
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<TrashIcon size={16} />}
                    className="self-end"
                    size="small"
                    onClick={() => setOpenDeleteDialog(true)}
                  >
                    Supprimer
                  </Button>
                  <Dialog open={openDeleteDialog} fullWidth maxWidth="sm">
                    <DialogTitle>Supprimer le lot</DialogTitle>
                    <DialogContent>
                      Etes-vous sûr de vouloir supprimer ce lot ?
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setOpenDeleteDialog(false)}>
                        Annuler
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleBatchDelete(request.batchId)}
                      >
                        Confirmer
                      </Button>
                    </DialogActions>
                  </Dialog>
                </Box>
                {index < requests.length - 1 && <Divider />}
              </React.Fragment>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

export default InsiBatchRequestList
